import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, ChevronLeft } from "lucide-react";

function fmt(d) {
  if (!d) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export default function CreateProjectPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => {
    if (user && !["admin", "project_coordinator", "project_manager"].includes(user.role)) {
      toast.error("Only Workspace Admins, Project Coordinators and Project Managers can create projects");
      navigate("/projects", { replace: true });
    }
  }, [user, navigate]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [pm, setPm] = useState("");
  const [customer, setCustomer] = useState("");
  const [brand, setBrand] = useState("");
  const [description, setDescription] = useState("");
  const [projectType, setProjectType] = useState("");
  const [target, setTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [previewNumber] = useState(() => {
    const y = new Date().getFullYear();
    return `${y}XXXX`;
  });

  const [customers, setCustomers] = useState([]);
  const [types, setTypes] = useState([]);
  useEffect(() => {
    api.get("/customers").then((r) => setCustomers(r.data.filter((c) => c.active !== false))).catch(() => {});
    api.get("/project-types", { params: { active_only: true } }).then((r) => setTypes(r.data.project_types || [])).catch(() => {});
    api.get("/users").then((r) => {
      setAllUsers(r.data.filter((u) => u.active !== false));
      const list = r.data.filter((u) => ["admin", "project_manager"].includes(u.role));
      setUsers(list);
      if (list.length && !pm) setPm(list[0].user_id);
    });
    // eslint-disable-next-line
  }, []);

  const title = useMemo(() => {
    return [customer, brand, projectType].filter(Boolean).join(" - ") || "Auto-generated from fields below";
  }, [customer, brand, projectType]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pm || !customer || !brand || !description || !projectType || !target) {
      toast.error("Please complete every field");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", {
        project_manager_id: pm,
        customer_name: customer,
        brand,
        description,
        project_type: projectType,
        target_date: fmt(target),
        member_ids: members.filter((m) => m !== pm),
      });
      toast.success(`Project ${data.project_number} created`);
      navigate(`/projects/${data.project_id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="px-4 sm:px-8 lg:px-12 py-8 max-w-4xl">
      <Link to="/projects" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900">
        <ChevronLeft className="h-4 w-4 mr-1" /> Back to registry
      </Link>

      <div className="mt-4">
        <p className="overline">New project</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Create project</h1>
        <p className="text-sm text-slate-500 mt-1">
          Project number is auto-generated once you confirm. Title is derived from the structured fields.
        </p>
      </div>

      <Card className="mt-6 p-6 border-slate-200">
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="create-project-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Project Number (auto)</Label>
              <Input
                readOnly
                value={previewNumber}
                data-testid="create-project-number-readonly"
                className="mt-1 font-mono bg-slate-50 border-slate-200"
              />
              <p className="text-[11px] text-slate-400 mt-1">Format: YYYYNNNN — generated server-side on save.</p>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Project Manager</Label>
              <Select value={pm} onValueChange={setPm}>
                <SelectTrigger className="mt-1" data-testid="create-project-pm-select">
                  <SelectValue placeholder="Select PM" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.name} <span className="text-slate-400 text-xs ml-1">({u.email})</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Customer</Label>
              <Input
                value={customer}
                onChange={(e) => setCustomer(e.target.value)}
                placeholder="e.g. Luxora Group"
                list="customer-suggestions"
                data-testid="create-project-customer-input"
                className="mt-1"
              />
              <datalist id="customer-suggestions">{customers.map((c) => <option key={c.customer_id} value={c.name} />)}</datalist>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Brand</Label>
              <Input
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                placeholder="e.g. North Line"
                list="brand-suggestions"
                data-testid="create-project-brand-input"
                className="mt-1"
              />
              <datalist id="brand-suggestions">{(customers.find((c) => c.name.toLowerCase() === customer.toLowerCase())?.brands || customers.flatMap((c) => c.brands || [])).map((b) => <option key={b} value={b} />)}</datalist>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-600">Project Type</Label>
              <Select value={projectType} onValueChange={setProjectType}>
                <SelectTrigger className="mt-1" data-testid="create-project-type-select">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {types.length === 0 ? (
                    <div className="px-3 py-2 text-xs text-slate-500">No active project types. Ask an admin to add them under Workspace Settings.</div>
                  ) : types.map((t) => (
                    <SelectItem key={t.type_id} value={t.name}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-slate-600">Target Date</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="create-project-target-date-picker"
                    className="mt-1 w-full justify-start font-normal border-slate-200"
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    {target ? fmt(target) : <span className="text-slate-400">Pick a date</span>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={target} onSelect={setTarget} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Team members (optional)</Label>
            <p className="text-[11px] text-slate-400 mt-0.5 mb-2">Members can access this project. The PM, Coordinators and Admins always have access.</p>
            <div className="rounded-md border border-slate-200 p-3 max-h-44 overflow-auto grid sm:grid-cols-2 gap-2" data-testid="create-project-members">
              {allUsers.filter((u) => u.user_id !== pm).length === 0 && <p className="text-xs text-slate-400">No other workspace users.</p>}
              {allUsers.filter((u) => u.user_id !== pm).map((u) => (
                <label key={u.user_id} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                  <Checkbox
                    checked={members.includes(u.user_id)}
                    onCheckedChange={(v) => setMembers((m) => (v ? [...m, u.user_id] : m.filter((x) => x !== u.user_id)))}
                    data-testid={`create-project-member-${u.user_id}`}
                  />
                  <span className="truncate">{u.name} <span className="text-slate-400 text-xs capitalize">({(u.role || "").replace(/_/g, " ")})</span></span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-xs text-slate-600">Project Description</Label>
            <Textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the scope, deliverables, constraints…"
              data-testid="create-project-description-input"
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-xs text-slate-600">Auto-generated Title</Label>
            <Input
              readOnly
              value={title}
              data-testid="create-project-generated-title-input"
              className="mt-1 bg-slate-50 border-slate-200 font-medium"
            />
          </div>

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="outline" onClick={() => navigate("/projects")}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              className="bg-[#2563EB] hover:bg-[#1d4ed8]"
              data-testid="create-project-submit-btn"
            >
              {submitting ? "Creating…" : "Create project"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
